import React, { useState, useEffect } from "react";
import {
  Sun,
  Moon,
  Github,
  Linkedin,
  Twitter,
  Mail,
  Code,
  User,
  MessageSquare,
  Briefcase,
  Globe,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Coffee,
  Award,
  BookOpen,
  Book,
  Star,
  FileText,
  Heart,
} from "lucide-react";

const Yash = () => {
  const [isDark, setIsDark] = useState(true);
  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const baseStyles = {
    bg: isDark ? "bg-slate-900" : "bg-slate-50",
    text: isDark ? "text-slate-100" : "text-slate-900",
    accent: isDark ? "text-rose-400" : "text-rose-600",
    card: isDark ? "bg-slate-800/50" : "bg-white",
    border: isDark ? "border-slate-700" : "border-slate-200",
    hover: isDark ? "hover:bg-slate-700" : "hover:bg-slate-100",
  };
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTypingPaused, setIsTypingPaused] = useState(false);

  const roles = [
    "Developer",
    "Frontend Engineer",
    "Innovative Problem Solver",
    "Business Strategist",
    "Creative Thinker",
  ];

  const baseStyles1 = {
    bg: "bg-slate-900",
    text: "text-slate-100",
    accent: "text-rose-400",
    card: "bg-slate-800/50",
    border: "border-slate-700",
    hover: "hover:bg-slate-700",
  };

  useEffect(() => {
    const typingSpeed = 50;
    const deletingSpeed = 50;
    const pauseDuration = 300;

    const animateText = () => {
      const currentWord = roles[currentIndex];

      if (!isDeleting && !isTypingPaused) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setIsTypingPaused(true);
          setTimeout(() => {
            setIsTypingPaused(false);
            setIsDeleting(true);
          }, pauseDuration);
        }
      } else if (isDeleting && !isTypingPaused) {
        if (displayText.length === 0) {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % roles.length);
        } else {
          setDisplayText(displayText.slice(0, -1));
        }
      }
    };

    const timer = setTimeout(
      animateText,
      isDeleting ? deletingSpeed : isTypingPaused ? pauseDuration : typingSpeed
    );

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, currentIndex, isTypingPaused]);

  const certificates = [
    {
      name: "Service Now",
      issuer: "CSA (Certified System Administrator)",
      year: "2023",
      color: "from-amber-500 to-orange-500",
      link: "https://drive.google.com/file/d/1gBNRq_1JXpricUELtUv22QLWzYP20T-g/view",
    },
    {
      name: "Service Now",
      issuer: "CAD (Certified Application Developer)",
      year: "2023",
      color: "from-blue-500 to-cyan-500",
      link: "https://drive.google.com/file/d/1bIHDF_sTUHJ8CkjF-yhOONEWFhDbrhAO/view",
    },
    {
      name: "Python",
      issuer: "Udemy",
      year: "2024",
      color: "from-indigo-500 to-purple-500",
      link: "https://drive.google.com/your-python-certificate-link",
    },
    {
      name: "Java",
      issuer: "Udemy",
      year: "2024",
      color: "from-indigo-500 to-purple-500",
      link: "https://drive.google.com/file/d/1yyi6qq8s3S9tFkSm2B-dIHrZo_y1vaM3/view",
    },
  ];

  const projects = [
    {
      title: "Plant Disease Detection",
      description:
        "A machine learning application that detects plant diseases.",
      tech: [
        "Python",
        "TensorFlow",
        "HTML",
        "CSS",
        "Flask API",
        "Machine Learning",
      ],
      image: "/plant.jpg",
    },
    {
      title: "Hotel Booking Website",
      description:
        "Customers can easily book their tables with secure authentication.",
      tech: ["MySQL", "Flask", "JavaScript", "HTML", "VS Code", "CSS"],
      image: "/hotel-booking.avif",
    },
    {
      title: "Smart Management of Drone Technology",
      description:
        "AI-powered Drone Technology.find the disease and solution with spray ",
      tech: ["IoT", "Firebase", "Python", "Machine Learning"],
      image: "/drone2.jpg",
    },
  ];

  const skills = [
    {
      category: "Frontend",
      items: ["React", "HTML", "CSS", "Javascript", "Bootstrap"],
      color: "text-rose-500",
    },
    {
      category: "Backend",
      items: ["Django", "Python", "Mysql"],
      color: "text-purple-500",
    },
    {
      category: "Python",
      items: ["Problem skills"],
      color: "text-cyan-500",
    },
  ];

  const timeline = [
    {
      year: "2019 - 2022",
      title: "Diploma",
      company: "Diploma Technical Education Board",
      details:
        "Engaged in comprehensive coursework covering computer architecture, algorithms, web development, and database management.gaining practical experience in tools like SQL, JavaScript.",
      side: "left",
      icon: <BookOpen size={22} />,
    },

    {
      year: "2023 - 2025",
      title: "Bachelor's in Computer Science and Engineering",
      company: "Dayananda Sagar University",
      details:
        "Developed and worked on several hands-on projects including a drone technology initiative focused on autonomous navigation and real-time data processing. Also, contributed to a database management system project designed to handle large-scale real-time data, strengthening skills in data storage, retrieval, and optimization.",
      side: "right",
      icon: <BookOpen size={20} />,
    },

    {
      year: "July 2023",
      title: "Python Developer",
      company: "Pheuna Technologies",
      details:
        "As a Python Developer at Pheuna Technologies, I was responsible for building and optimizing backend services using Python. I developed and maintained APIs using Flask/Django, integrated databases like MySQL and MongoDB, and implemented automation scripts to enhance workflow efficiency. Additionally, I worked on real-time data processing, optimized query performance, and collaborated with front-end teams to ensure seamless application functionality. My contributions improved system performance, reduced response times, and enhanced overall application scalability.",
      side: "left",
      icon: <Code size={22} />,
    },
    {
      year: "2025",
      title: "Smart Management of Crop Monitoring Using Drone Technology",
      company: "Competitive Achievement",
      details:
        "Developed an advanced crop monitoring system using drone technology to enhance precision farming. The project focused on real-time data collection for detecting plant diseases, monitoring crop health, and optimizing fertilizer usage. Integrated machine learning algorithms to analyze captured images, providing actionable insights for farmers. Utilized IoT and cloud computing for seamless data storage and accessibility, improving agricultural efficiency and sustainability.",
      side: "right",
      icon: <Award size={22} />,
    },
  ];

  return (
    <div className={`${baseStyles.bg} ${baseStyles.text} min-h-screen`}>
      {/* Header */}
      <header className="fixed w-full z-50 px-6 py-4">
        <nav
          className={`${baseStyles.card} rounded-2xl border ${baseStyles.border} p-4 backdrop-blur-lg`}
        >
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">
              Port<span className={baseStyles.accent}>folio</span>
            </h1>
            <div className="hidden lg:flex items-center gap-6">
              <a
                href="#about"
                className={baseStyles.hover + " px-4 py-2 rounded-lg"}
              >
                About
              </a>
              <a
                href="#projects"
                className={baseStyles.hover + " px-4 py-2 rounded-lg"}
              >
                Projects
              </a>
              <a
                href="#education"
                className={baseStyles.hover + " px-4 py-2 rounded-lg"}
              >
                Education
              </a>
              <a
                href="#certificates"
                className={baseStyles.hover + " px-4 py-2 rounded-lg"}
              >
                Certificates
              </a>
              <a
                href="#contact"
                className={baseStyles.hover + " px-4 py-2 rounded-lg"}
              >
                Contact
              </a>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg ${baseStyles.hover}`}
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="about" className="pt-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <div className="space-y-6">
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full ${baseStyles.card} border ${baseStyles.border}`}
              >
                <Coffee className={baseStyles1.accent} size={20} />
                <span className="ml-2">Available for Projects</span>
              </div>
              <h2 className="text-5xl font-bold leading-tight">
                Yashwanth G M
                <div
                  className={`block ${baseStyles1.accent} text-4xl mt-2 min-h-[72px]`}
                >
                  is a{" "}
                  <span className="inline-flex">
                    {displayText}
                    <span className="ml-1 animate-pulse">|</span>
                  </span>
                </div>
              </h2>

              <p className="text-lg opacity-80 max-w-xl">
                As a passionate technologist with a creative edge, I transform
                complex problems into elegant solutions. With expertise in
                full-stack development and a keen eye for design, I create
                seamless digital experiences that leave a lasting impact. My
                approach combines technical excellence with innovative thinking
                to build solutions that not only work flawlessly but also
                inspire and engage users.
              </p>

              <div className="flex gap-4">
                <a
                  href="#contact"
                  className={`px-6 py-3 rounded-lg bg-rose-500 text-white hover:bg-rose-600 transition-colors`}
                >
                  Get in Touch
                </a>
                <a
                  href="/YASHWANTH-FlowCV-Resume-20250220 (3).pdf"
                  target="_blank"
                  className={`px-6 py-3 rounded-lg border ${baseStyles.border} ${baseStyles.hover}`}
                >
                  View Resume
                </a>
              </div>
            </div>

            {/* Right side - Image content */}
            <div
              className={`${baseStyles.card} rounded-2xl border ${baseStyles.border} relative group h-full`}
            >
              <div className="aspect-square w-full h-full relative overflow-hidden rounded-xl">
                <img
                  src="/WhatsApp Image 2025-02-20 at 14.06.56_f7efd608.jpg"
                  alt="Profile"
                  className="w-full h-full object-cover object-center rounded-xl"
                />
                {/* Optional overlay effect */}
                {/* <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 to-purple-500/10 group-hover:opacity-75 transition-opacity" /> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold mb-12">Projects</h3>
          <a href="https://github.com/yashwanth2000GM" target="_blank">
            <div className="grid lg:grid-cols-3 gap-8 cursor">
              {projects.map((project) => (
                <div
                  key={project.title}
                  className={`${baseStyles.card} rounded-xl border ${baseStyles.border} p-6 group hover:scale-105 transition-all duration-300`}
                >
                  <div className="h-48 rounded-lg overflow-hidden mb-6">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                    />
                  </div>
                  <h4 className="text-xl font-bold mb-3">{project.title}</h4>
                  <p className="opacity-80 mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className={`px-3 py-1 rounded-full text-sm ${baseStyles.hover}`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </a>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent to-slate-800/20">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold mb-12">Skills & Expertise</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {skills.map((category) => (
              <div
                key={category.category}
                className={`${baseStyles.card} rounded-xl border ${baseStyles.border} p-6`}
              >
                <h4 className={`text-xl font-bold mb-4 ${category.color}`}>
                  {category.category}
                </h4>
                <div className="space-y-3">
                  {category.items.map((skill) => (
                    <div
                      key={skill}
                      className={`${baseStyles.hover} rounded-lg p-3 flex items-center gap-3`}
                    >
                      <Star size={16} className={category.color} />
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* About Section */}

      {/* Education Section */}
      <section id="education" className="py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12">Journey So Far</h2>
          <div className="relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-teal-500 to-purple-500" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={item.title}
                  className={`
            flex items-center gap-8
            ${
              item.side === "left"
                ? "flex-col-reverse md:flex-row-reverse"
                : "flex-col md:flex-row"
            }
          `}
                >
                  {/* Content Section */}
                  <div
                    className={`
              w-full md:w-1/2 
              ${item.side === "left" ? "text-left" : "text-right"}
            `}
                  >
                    <div
                      className={`
                ${baseStyles.card} 
                p-6 rounded-xl border ${baseStyles.border}
                transform hover:scale-105 transition-all duration-300
              `}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {item.icon}
                        <span className={`text-sm ${baseStyles.accent}`}>
                          {item.year}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-1">{item.title}</h3>
                      <div className="text-sm font-medium opacity-80 mb-2">
                        {item.company}
                      </div>
                      <p className="text-sm opacity-60">{item.details}</p>
                    </div>
                  </div>

                  {/* Timeline Dot */}
                  <div className="relative">
                    <div className="w-4 h-4 rounded-full bg-teal-500 relative z-10">
                      <div className="absolute inset-0 rounded-full bg-teal-500 animate-ping" />
                    </div>
                  </div>

                  {/* Spacer Section (for layout on larger screens) */}
                  <div className="w-full md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certificates Section */}
      <section id="certificates" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-12">
            <Award className={baseStyles.accent} size={32} />
            <h3 className="text-3xl font-bold">Certificates</h3>
          </div>
          <div className="grid lg:grid-cols-3 gap-8 cursor">
            {certificates.map((cert, index) => (
              <a
                key={index}
                href={cert.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`${baseStyles.card} rounded-xl border ${baseStyles.border} p-6 relative overflow-hidden group block`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${cert.color} opacity-10 group-hover:opacity-20 transition-opacity`}
                />
                <Award className={baseStyles.accent} size={24} />
                <h4 className="text-xl font-bold mt-4 mb-2">{cert.name}</h4>
                <p className="opacity-80 mb-2">{cert.issuer}</p>
                <p className="text-sm opacity-60">{cert.year}</p>
                <button className="mt-4 px-4 py-2 flex items-center gap-2 text-white font-medium transition-all duration-300 hover:text-gray-300">
                  Take a Look
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </button>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div
            className={`${baseStyles.card} rounded-2xl border ${baseStyles.border} p-8 lg:p-12`}
          >
            <div className="flex items-center gap-4 mb-8">
              <Mail className={baseStyles.accent} size={32} />
              <h3 className="text-3xl font-bold">Get in Touch</h3>
            </div>
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                <p className="text-lg">
                  Have a project in mind? Let's work together to create
                  something amazing. Feel free to reach out through any of these
                  channels:
                </p>
                <div className="space-y-4">
                  <a
                    href="mailto:contact@example.com"
                    className="flex items-center gap-3 opacity-80 hover:opacity-100"
                  >
                    <Mail size={20} /> gmyashwanth2000@gmail.com
                  </a>
                  <div className="flex gap-4">
                    <a
                      href="https://github.com/yashwanth2000GM"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${baseStyles.hover} p-2 rounded-lg`}
                    >
                      <Github size={20} />
                    </a>
                    <a
                      href="https://x.com/YashwanthG19044"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${baseStyles.hover} p-2 rounded-lg`}
                    >
                      <Twitter size={20} />
                    </a>
                    <a
                      href="https://www.linkedin.com/in/yashwanth-gm-96227b280"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${baseStyles.hover} p-2 rounded-lg`}
                    >
                      <Linkedin size={20} />
                    </a>
                    <a
                      href="mailto:gmyashwanth2000@gmail.com"
                      className={`${baseStyles.hover} p-2 rounded-lg`}
                    >
                      <Mail size={20} />
                    </a>
                  </div>
                </div>
              </div>
              <form className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Name</label>
                  <input
                    type="text"
                    className={`w-full px-4 py-2 rounded-lg ${baseStyles.card} border ${baseStyles.border} focus:outline-none focus:ring-2 focus:ring-rose-500`}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Email</label>
                  <input
                    type="email"
                    className={`w-full px-4 py-2 rounded-lg ${baseStyles.card} border ${baseStyles.border} focus:outline-none focus:ring-2 focus:ring-rose-500`}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Message</label>
                  <textarea
                    rows={4}
                    className={`w-full px-4 py-2 rounded-lg ${baseStyles.card} border ${baseStyles.border} focus:outline-none focus:ring-2 focus:ring-rose-500`}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 rounded-lg bg-rose-500 text-white hover:bg-rose-600 transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 border-t ${baseStyles.border}`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <p className="text-sm opacity-60">
            © {new Date().getFullYear()} | Built with Yashwanth G M{" "}
            <Heart size={14} className="inline text-rose-500" />
          </p>

          <div className="flex gap-4">
            <a
              href="https://github.com/yashwanth2000GM"
              target="_blank"
              rel="noopener noreferrer"
              className={`${baseStyles.hover} p-2 rounded-lg`}
            >
              <Github size={20} />
            </a>
            <a
              href="https://x.com/YashwanthG19044"
              target="_blank"
              rel="noopener noreferrer"
              className={`${baseStyles.hover} p-2 rounded-lg`}
            >
              <Twitter size={20} />
            </a>
            <a
              href="https://www.linkedin.com/in/yashwanth-gm-96227b280"
              target="_blank"
              rel="noopener noreferrer"
              className={`${baseStyles.hover} p-2 rounded-lg`}
            >
              <Linkedin size={20} />
            </a>
            <a
              href="mailto:gmyashwanth2000@gmail.com"
              className={`${baseStyles.hover} p-2 rounded-lg`}
            >
              <Mail size={20} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Yash;
